import React, { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import {
  Box,
  Grid,
  InputAdornment,
  TablePagination,
  TextField,
  Typography,
} from '@material-ui/core'
import { Search as SearchIcon } from 'react-feather'
import { isEmpty } from 'lodash'

import { Card, LoadingFeedback } from 'components'
import { CourseCard } from './components'

import useFetch from 'hooks/useFetch'
import usePhishing from 'hooks/usePhishing'
import useSnackbar from 'hooks/useSnackbar'
import * as service from 'service'
import constants from 'constants/index'

import schema from './schema'
import useStyles from './styles'

const ROWS_PER_PAGE_OPTIONS = [4, 8, 12]

const ConfigStep = () => {
  const classes = useStyles()
  const [loading, setLoading] = useState(false)
  const [selectedTrainingId, setSelectedTrainingId] = useState(null)
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [courseError, setCourseError] = useState(false)
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(4)

  const snackbar = useSnackbar()
  const {
    phishing,
    setPhishing,
    handleNext,
    isReadOnly,
    wantsToAdvanceStep,
    resetAdvanceStep,
  } = usePhishing()

  const { control, errors, getValues, triggerValidation } = useForm({
    validationSchema: schema,
    defaultValues: {
      name: phishing?.name || '',
    },
  })

  const { response: coursesResponse, isLoading: isCoursesLoading } = useFetch(
    service.dponet.trainingCourse.index,
    { page, perPage, title: debouncedSearch || undefined },
    [page, perPage, debouncedSearch],
  )

  const courses = coursesResponse?.data?.courses || []
  const totalCount = coursesResponse?.data?.meta?.totalCount || 0

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput)
      setPage(1)
    }, 500)

    return () => clearTimeout(timer)
  }, [searchInput])

  useEffect(() => {
    if (phishing?.trainingId !== undefined) {
      setSelectedTrainingId(phishing.trainingId ?? null)
    }
  }, [phishing?.trainingId])

  const handleChangePage = (event, newPage) => {
    setPage(newPage + 1)
  }

  const handleChangeRowsPerPage = (event) => {
    setPerPage(parseInt(event.target.value, 10))
    setPage(1)
  }

  const handleSelectCourse = (courseId) => {
    setSelectedTrainingId(courseId)
    setCourseError(false)
  }

  const onSubmit = async () => {
    const isValid = await triggerValidation()

    if (!isValid) {
      resetAdvanceStep()
      return
    }

    setLoading(true)

    try {
      const { name } = getValues()

      const response = await (phishing?.id
        ? service.dponet.phishingCampaigns.configStep(phishing.id, {
            phishingCampaign: { name, trainingId: selectedTrainingId },
          })
        : service.dponet.phishingCampaigns.create({
            phishingCampaign: {
              name,
              trainingId: selectedTrainingId,
              modelId: constants.phishing.HARDCODED_MODEL_ID,
            },
          }))
      const campaign = response.data.phishingCampaign

      setPhishing((previous) => ({
        ...previous,
        ...campaign,
        name,
        trainingId: selectedTrainingId,
      }))
      resetAdvanceStep()
      handleNext()
    } catch {
      snackbar.open({
        message: 'Erro ao salvar configurações da campanha.',
        variant: 'error',
      })
      resetAdvanceStep()
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (wantsToAdvanceStep) {
      onSubmit()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantsToAdvanceStep])

  return (
    <>
      <LoadingFeedback open={loading || isCoursesLoading} />
      <Card>
        <Box p={2}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Controller
                as={
                  <TextField
                    label="Nome da campanha"
                    placeholder="Digite um nome para sua campanha"
                    variant="outlined"
                    fullWidth
                    disabled={isReadOnly}
                    error={!!errors.name}
                    helperText={errors?.name?.message}
                  />
                }
                name="name"
                control={control}
              />
            </Grid>

            <Grid item xs={12}>
              <Box mb={1}>
                <Typography variant="h4">Conscientização</Typography>
              </Box>
              <Typography variant="body2" color="textSecondary">
                Escolha um curso de conscientização para a campanha. Quando um
                colaborador cair na simulação, poderá acessá-lo a partir da
                página de aviso.
              </Typography>

              <Box mt={2}>
                <TextField
                  fullWidth
                  placeholder="Buscar curso por nome"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  variant="outlined"
                  disabled={isReadOnly}
                  error={courseError}
                  helperText={
                    courseError
                      ? 'Selecione um curso de conscientização.'
                      : undefined
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon size={20} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Box mt={2}>
                {!isCoursesLoading && isEmpty(courses) ? (
                  <Typography className={classes.emptyState}>
                    {isEmpty(debouncedSearch)
                      ? 'Nenhum curso disponível.'
                      : 'Nenhum curso encontrado para a busca.'}
                  </Typography>
                ) : (
                  <>
                    <Grid container spacing={2}>
                      {courses.map((course) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={course.id}>
                          <CourseCard
                            course={course}
                            isSelected={selectedTrainingId === course.id}
                            disabled={isReadOnly}
                            onSelect={handleSelectCourse}
                          />
                        </Grid>
                      ))}
                    </Grid>

                    <TablePagination
                      component="div"
                      count={totalCount}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      page={page - 1}
                      rowsPerPage={perPage}
                      rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                      labelRowsPerPage="Por página"
                      labelDisplayedRows={({ from, to, count }) =>
                        `${from}-${to} de ${count}`
                      }
                      nextIconButtonProps={{ size: 'small' }}
                      backIconButtonProps={{ size: 'small' }}
                    />
                  </>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Card>
    </>
  )
}

export default ConfigStep
